import {
  REDUB_LANDED_CLASS,
  REDUB_LANDED_PULSE_MS,
  wordsOf,
} from '@src/components/aiVoices/RedubLandedPulse';
import { REVIEW_HANDOFF_WAIT_MS } from '@src/components/aiVoices/reviewHandoff';
import { useEffect, useRef } from 'react';

import type { ReDubSelectionRequest } from './useSpanReplace';
import type { ReviewHandoff } from '@src/components/aiVoices/reviewHandoff';
import type { RefObject } from 'react';

interface ReviewHandoffOptions {
  /** The host's hand-off, until it is consumed. */
  pendingHandoff: ReviewHandoff | null | undefined;
  /** The review's translated column: where the handed-off words are found. */
  rootRef: RefObject<HTMLElement>;
  /** Opens the Redub Selection popover on the found words. */
  openRedub: (request: ReDubSelectionRequest) => void;
  /** Opens the Fix Translation editor over the covered words. */
  openFix: (coveredIndices: readonly number[]) => void;
  /** Tells the host to drop the hand-off. */
  onConsumed?: () => void;
}

/**
 * PLAT-12047 / PLAT-12056 — act once on a hand-off from a host transcript:
 * wait for its words to render in this review, light them (re-dub only),
 * scroll to them, open the popover or editor on them, and report it consumed.
 *
 * Keyed on the hand-off's nonce only: the callbacks are read through refs, so
 * a host re-render mid-wait does not restart the wait. The wash lives outside
 * the per-hand-off effect, so the host dropping the consumed hand-off (which
 * re-runs that effect) does not clear it; it fades on its own timer, gives way
 * to the next hand-off's, or goes on unmount.
 */
export function useReviewHandoff({
  pendingHandoff,
  rootRef,
  openRedub,
  openFix,
  onConsumed,
}: ReviewHandoffOptions): void {
  const openRedubRef = useRef(openRedub);
  openRedubRef.current = openRedub;
  const openFixRef = useRef(openFix);
  openFixRef.current = openFix;
  const onConsumedRef = useRef(onConsumed);
  onConsumedRef.current = onConsumed;
  const pendingRef = useRef(pendingHandoff);
  pendingRef.current = pendingHandoff;

  const washRef = useRef<{
    words: HTMLElement[];
    fade: ReturnType<typeof setTimeout>;
  } | null>(null);
  const clearWash = () => {
    const wash = washRef.current;
    if (!wash) return;
    clearTimeout(wash.fade);
    for (const word of wash.words) word.classList.remove(REDUB_LANDED_CLASS);
    washRef.current = null;
  };
  const lightWords = (words: HTMLElement[]) => {
    clearWash();
    for (const word of words) word.classList.add(REDUB_LANDED_CLASS);
    washRef.current = {
      words,
      fade: setTimeout(clearWash, REDUB_LANDED_PULSE_MS),
    };
  };
  const clearWashRef = useRef(clearWash);
  clearWashRef.current = clearWash;
  const lightWordsRef = useRef(lightWords);
  lightWordsRef.current = lightWords;
  useEffect(() => () => clearWashRef.current(), []);

  const doneRef = useRef(0);
  const nonce = pendingHandoff?.nonce ?? 0;
  useEffect(() => {
    const handoff = pendingRef.current;
    if (!handoff || nonce === doneRef.current) return undefined;
    let cancelled = false;
    let poll: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();
    const finish = (anchorElement: HTMLElement | null) => {
      if (cancelled) return;
      doneRef.current = handoff.nonce;
      if (handoff.kind === 'redub') {
        if (anchorElement) {
          openRedubRef.current({
            segmentIds: handoff.segmentIds,
            speakerLabels: handoff.speakerLabels,
            anchorElement,
            translatedText: handoff.translatedText,
            sourceText: handoff.sourceText,
          });
        }
      } else {
        // The editor anchors itself on the covered words (PLAT-12056).
        openFixRef.current(handoff.coveredIndices);
      }
      onConsumedRef.current?.();
    };
    const findWords = (root: HTMLElement): HTMLElement[] =>
      handoff.kind === 'redub'
        ? wordsOf(root, handoff.segmentIds)
        : handoff.coveredIndices.flatMap(index =>
            Array.from(
              root.querySelectorAll<HTMLElement>(
                `[data-initial-index="${index}"]`,
              ),
            ),
          );
    const attempt = () => {
      if (cancelled) return;
      const root = rootRef.current;
      const words = root ? findWords(root) : [];
      if (words.length === 0) {
        if (Date.now() - startedAt < REVIEW_HANDOFF_WAIT_MS) {
          poll = setTimeout(attempt, 150);
        } else {
          finish(root);
        }
        return;
      }
      if (handoff.kind === 'redub') lightWordsRef.current(words);
      if (typeof words[0].scrollIntoView === 'function') {
        words[0].scrollIntoView({ block: 'center' });
      }
      // Anchor once the scroll has settled, or the popover is placed
      // against where the word was.
      requestAnimationFrame(() => finish(words[0]));
    };
    attempt();
    return () => {
      cancelled = true;
      if (poll) clearTimeout(poll);
    };
  }, [nonce, rootRef]);
}
